import { useState, useEffect, Suspense, useCallback, Component } from 'react';
import { Canvas } from '@react-three/fiber';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Volume2, VolumeX, Info, Keyboard, RefreshCw } from 'lucide-react';
import BeautyStudioScene, { CONSULTANT_STATES } from '../components/3d/BeautyStudioScene';
import CameraController from '../components/3d/CameraController';
import GlassmorphismChatPanel from '../components/3d/GlassmorphismChatPanel';

// Suppress specific R3F errors in development
if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0] && typeof args[0] === 'string' && 
        (args[0].includes('x-line-number') || 
         args[0].includes('Cannot set') ||
         args[0].includes('Cannot convert undefined or null'))) {
      return;
    }
    originalError.apply(console, args);
  };
  
  window.addEventListener('error', (event) => {
    if (event.message && 
        (event.message.includes('x-line-number') || 
         event.message.includes('Cannot convert undefined'))) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
  }, true);
}

// Error Boundary for 3D Canvas
class Canvas3DErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    if (!error.message?.includes('x-line-number') && 
        !error.message?.includes('Cannot convert undefined')) {
      console.error('3D Canvas Error:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="absolute inset-0 bg-gradient-to-br from-[#FDF8F6] via-white to-[#F5F0EB] flex items-center justify-center">
          <div className="text-center p-8">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#D4A5A5] to-[#BA7B7C] rounded-2xl flex items-center justify-center">
              <span className="text-3xl">💄</span>
            </div>
            <h2 className="text-xl font-light text-[#1C1917] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              No se pudo cargar el estudio 3D
            </h2>
            <p className="text-sm text-[#A8A29E] mb-4">
              Tu navegador puede no soportar WebGL.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center space-x-2 mx-auto px-4 py-2 bg-[#BA7B7C] text-white rounded-lg hover:bg-[#A56969] transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Recargar</span>
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading Screen Component
const LoadingScreen = () => (
  <div className="absolute inset-0 bg-gradient-to-br from-[#FDF8F6] via-white to-[#F5F0EB] flex items-center justify-center z-50">
    <div className="text-center">
      <div className="w-20 h-20 mx-auto mb-6 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4A5A5] to-[#BA7B7C] rounded-2xl animate-pulse" />
        <div className="absolute inset-2 bg-white/90 rounded-xl flex items-center justify-center">
          <span className="text-2xl">💄</span>
        </div>
      </div>
      <h2 
        className="text-2xl font-light text-[#1C1917] mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        Cargando Beauty Studio
      </h2>
      <p className="text-sm text-[#A8A29E]">Preparando tu experiencia de belleza...</p>
      <div className="mt-4 w-48 h-1 bg-[#E7E5E4] rounded-full mx-auto overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#D4A5A5] to-[#BA7B7C] rounded-full animate-pulse" />
      </div>
    </div>
  </div>
);

// Controls Help Overlay
const ControlsHelp = ({ isVisible, onClose }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      className="absolute bottom-24 left-4 z-40 bg-white/90 backdrop-blur-md rounded-2xl p-5 shadow-xl border border-[#E7E5E4] max-w-xs animate-fade-in"
    >
      <h3 className="text-sm font-medium text-[#1C1917] mb-3 flex items-center space-x-2">
        <Keyboard className="w-4 h-4 text-[#BA7B7C]" />
        <span>Controles</span>
      </h3>
      
      <div className="space-y-2 text-xs text-[#57534E]">
        <div className="flex items-center justify-between">
          <span>Mover cámara</span>
          <span className="font-mono bg-[#F5F0EB] px-2 py-0.5 rounded">W A S D</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Correr</span>
          <span className="font-mono bg-[#F5F0EB] px-2 py-0.5 rounded">Shift</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Rotar vista</span>
          <span className="font-mono bg-[#F5F0EB] px-2 py-0.5 rounded">Arrastrar</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Interactuar</span>
          <span className="font-mono bg-[#F5F0EB] px-2 py-0.5 rounded">E</span>
        </div>
      </div>
      
      <button
        onClick={onClose}
        className="mt-3 w-full text-xs text-[#A8A29E] hover:text-[#BA7B7C] transition-colors"
      >
        Cerrar
      </button>
    </div>
  );
};

// Interaction Hint
const InteractionHint = ({ isVisible, isMobile }) => {
  if (!isVisible) return null;
  
  return (
    <div className="absolute bottom-1/3 left-1/2 transform -translate-x-1/2 z-30 pointer-events-none">
      <div className="bg-white/90 backdrop-blur-md rounded-xl px-4 py-2 shadow-lg border border-[#E7E5E4] animate-pulse">
        <p className="text-sm text-[#1C1917] flex items-center space-x-2">
          <span className="text-[#BA7B7C]">✨</span>
          <span>{isMobile ? 'Toca el espejo para interactuar' : 'Presiona E cerca del espejo'}</span>
        </p>
      </div>
    </div>
  );
};

const GlamAdvisor = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMirrorActive, setIsMirrorActive] = useState(false);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [showInteractionHint, setShowInteractionHint] = useState(true);
  const [highlightedProducts, setHighlightedProducts] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  
  // Phase 3: Beauty Consultant State
  const [consultantState, setConsultantState] = useState(CONSULTANT_STATES.IDLE);
  const [showWelcome, setShowWelcome] = useState(false);
  const [spellCheckData, setSpellCheckData] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2500);
    
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isChatOpen) {
      setShowInteractionHint(false);
    }
  }, [isChatOpen]);

  const handleMirrorInteract = useCallback(() => {
    setIsMirrorActive(true);
    setIsChatOpen(true);
    setShowInteractionHint(false);
    setShowWelcome(true);
    setConsultantState(CONSULTANT_STATES.LOOKING_AT_USER);
    setTimeout(() => {
      setShowWelcome(false);
    }, 3000);
  }, []);

  const handleCloseChat = useCallback(() => {
    setIsChatOpen(false);
    setIsMirrorActive(false);
    setConsultantState(CONSULTANT_STATES.IDLE);
    setSpellCheckData(null);
  }, []);

  const handleSpellCheckConfirm = useCallback((correctedText) => {
    setSpellCheckData(null);
  }, []);

  const handleSpellCheckEdit = useCallback(() => {
    setSpellCheckData(null);
  }, []);

  const handleProductsRecommended = useCallback((products) => {
    const productTypes = products.map(p => {
      const name = p.name.toLowerCase();
      if (name.includes('perfume') || name.includes('ariana') || name.includes('fragancia')) {
        return `perfume${Math.floor(Math.random() * 6) + 1}`;
      }
      if (name.includes('labial') || name.includes('lipstick') || name.includes('labios')) {
        return `lipstick${Math.floor(Math.random() * 4) + 1}`;
      }
      return null;
    }).filter(Boolean);
    
    setHighlightedProducts(productTypes);
    
    setTimeout(() => {
      setHighlightedProducts([]);
    }, 10000);
  }, []);

  return (
    <div className="fixed inset-0 bg-[#FDF8F6]" data-testid="glam-advisor-3d-page">
      {/* Loading Screen */}
      {isLoading && <LoadingScreen />}
      
      {/* 3D Canvas */}
      <Canvas3DErrorBoundary>
        <Canvas
          shadows
          dpr={[1, isMobile ? 1.5 : 2]}
          style={{ background: '#FDF8F6' }}
          gl={{ antialias: true }}
          camera={{ position: [0, 1.6, 3], fov: 60, near: 0.1, far: 100 }}
        >
          <Suspense fallback={null}>
            <BeautyStudioScene 
              isMirrorActive={isMirrorActive}
              isAIThinking={isAIThinking}
              onMirrorInteract={handleMirrorInteract}
              highlightedProducts={highlightedProducts}
              consultantState={consultantState}
              showWelcome={showWelcome}
              spellCheckData={spellCheckData}
              onSpellCheckConfirm={handleSpellCheckConfirm}
              onSpellCheckEdit={handleSpellCheckEdit}
              products={products}
            />
            <CameraController 
              isMirrorActive={isMirrorActive}
              onApproachMirror={handleMirrorInteract}
            />
          </Suspense>
        </Canvas>
      </Canvas3DErrorBoundary>
      
      {/* Top Navigation Bar */}
      <div className="absolute top-0 left-0 right-0 z-40">
        <div className="bg-gradient-to-b from-black/50 via-black/30 to-transparent py-4 px-6">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <Link 
              to="/" 
              className="flex items-center space-x-2 text-white hover:text-white/80 transition-colors drop-shadow-md"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Volver a la tienda</span>
            </Link>
            
            <div className="flex items-center space-x-2">
              <h1 
                className="text-xl font-light text-white tracking-wide drop-shadow-md"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                AliGlam Beauty Studio
              </h1>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowControls(!showControls)}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                title="Controles"
              >
                <Info className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsMuted(!isMuted)}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                title={isMuted ? "Activar sonido" : "Silenciar"}
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Controls Help */}
      <ControlsHelp isVisible={showControls} onClose={() => setShowControls(false)} />
      
      {/* Interaction Hint */}
      <InteractionHint isVisible={showInteractionHint && !isLoading} isMobile={isMobile} />
      
      {/* Open Chat Button (when chat is closed) */}
      {!isChatOpen && !isLoading && (
        <button
          onClick={handleMirrorInteract}
          className="absolute bottom-6 right-6 z-40 flex items-center space-x-2 bg-gradient-to-r from-[#D4A5A5] to-[#BA7B7C] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
          data-testid="open-chat-btn"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm font-medium">Hablar con Beauty Advisor</span>
        </button>
      )}
      
      {/* Glassmorphism Chat Panel */}
      <GlassmorphismChatPanel
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        isAIThinking={isAIThinking}
        setIsAIThinking={setIsAIThinking}
        onProductsRecommended={handleProductsRecommended}
        isMuted={isMuted}
        onToggleMute={() => setIsMuted(!isMuted)}
      />
      
      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/40 to-transparent pointer-events-none z-10" />
      
      {/* Mobile Touch Hint */}
      {isMobile && !isChatOpen && !isLoading && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30">
          <p className="text-xs text-white/60 text-center">
            Arrastra para explorar
          </p>
        </div>
      )}
    </div>
  );
};

export default GlamAdvisor;
